import axios from "axios";
import { DateTime } from "luxon";
import { ENV } from "@/shared/utils/constants";
import { FullUserResponse } from "@/redux/users/types";

export const LocalStorageName = `prme-commons-${ENV}`;

export const clientApi = axios.create({
  baseURL: import.meta.env.VITE_APP_BACKEND_URL,
  withCredentials: true,
});

export const refreshApi = axios.create({
  baseURL: import.meta.env.VITE_APP_BACKEND_URL,
  withCredentials: true,
});

clientApi.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalRequest = error.config;
    if (error?.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const refreshRequest = await refreshApi.post("/auth/refresh/");
        window.localStorage.setItem(
          LocalStorageName,
          JSON.stringify({
            accessExpiration: refreshRequest.data.accessExpiration,
            refreshExpiration: refreshRequest.data.refreshExpiration,
            user: refreshRequest.data.user,
          }),
        );
        return clientApi(originalRequest);
      } catch (err) {
        window.localStorage.removeItem(LocalStorageName);
        if (!window.location.href.includes("public")) {
          const url = new URL(window.location.href);
          const params = url.searchParams.get("redirect_to")
            ? `?redirect_to=${url.searchParams.get("redirect_to")}`
            : "";
          window.location.href = "/login" + params;
        }
        throw err;
      }
    }
    throw error;
  },
);

export const createUrl = (
  baseUrl: string,
  queryParams: void | Record<string, any>,
  excludeValues = [null, undefined],
) => {
  if (!queryParams) return baseUrl;

  const params = convertToQueryParamsString(queryParams, excludeValues);

  return params === "" ? baseUrl : baseUrl + "?" + params;
};

export const convertToQueryParamsString = (
  queryParams: Record<string, any>,
  excludeValues: (string | null | undefined | number)[],
) => {
  const newQueryParams: Record<string, any> = {};
  Object.keys(queryParams)
    .filter((key) => !excludeValues.includes(queryParams[key]))
    .map(
      (key) =>
        (newQueryParams[
          key.replace(/[A-Z]/g, (match) => "_" + match.toLowerCase())
        ] = queryParams[key]),
    );
  return new URLSearchParams(newQueryParams).toString();
};

export const isUserAuthenticated = () => {
  const token = window.localStorage.getItem(LocalStorageName);
  if (!token) return false;

  try {
    const tokenStorage = JSON.parse(token);
    if (tokenStorage?.accessExpiration && tokenStorage?.refreshExpiration) {
      const accessTokenExpiration = DateTime.fromSeconds(
        tokenStorage.accessExpiration,
      );
      const refreshTokenExpiration = DateTime.fromSeconds(
        tokenStorage.refreshExpiration,
      );
      const currentTime = DateTime.now();

      return (
        accessTokenExpiration > currentTime ||
        refreshTokenExpiration > currentTime
      );
    }
    return false;
  } catch (e) {
    console.error(e);
    return false;
  }
};

export const getUserFromStorage = (): FullUserResponse | null => {
  try {
    const stored = window.localStorage.getItem(LocalStorageName);
    if (!stored) return null;

    const parsedData = JSON.parse(stored);
    return parsedData?.user || null;
  } catch (error) {
    console.error("Error parsing user from localStorage:", error);
    return null;
  }
};
